import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, FileX, Eye, FileText, Download, ClipboardCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Visualizzatore di documenti in sola consultazione.
 *
 * Mostra le pagine come immagini con filigrana (rese dal server): il file
 * originale non arriva mai al browser. Niente pulsanti di download o stampa,
 * niente trascinamento, niente menu contestuale sull'area del documento.
 * Le immagini si chiedono con fetch autenticato e diventano blob URL, revocati
 * alla chiusura.
 *
 * Nota onesta: nessun browser può impedire del tutto uno screenshot. Qui si
 * evita di offrire il file e ogni copia porta scritto il nome di chi l'ha vista.
 */
export default function VisualizzatoreDocumento({ open, onOpenChange, condominiumId, documentId, titoloIniziale }) {
  const { token } = useAuth();
  const [info, setInfo] = useState(null);
  const [errore, setErrore] = useState(null);
  const [pagine, setPagine] = useState([]); // blob URL per pagina (null = non ancora arrivata)
  const [corrente, setCorrente] = useState(1);
  const [ricarica, setRicarica] = useState(0);        // si incrementa dopo la presa visione per rileggere il documento
  const [inConferma, setInConferma] = useState(false);
  const [erroreConferma, setErroreConferma] = useState(null);
  const contenitore = useRef(null);
  const urlCreati = useRef([]);

  const base = `${API_URL}/api/condominiums/${condominiumId}/consultazione/documenti/${encodeURIComponent(documentId || '')}`;

  const pulisci = useCallback(() => {
    urlCreati.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* gia' revocato */ } });
    urlCreati.current = [];
  }, []);

  useEffect(() => {
    if (!open || !documentId || !condominiumId || !token) return undefined;
    const controller = new AbortController();
    let attivo = true;
    setInfo(null); setErrore(null); setPagine([]); setCorrente(1);

    const intestazioni = { Authorization: `Bearer ${token}` };

    (async () => {
      try {
        const r = await fetch(`${base}/info`, { headers: intestazioni, signal: controller.signal });
        if (!r.ok) {
          setErrore('Questo documento non e\' disponibile per te.');
          return;
        }
        const dati = await r.json();
        if (!attivo) return;
        setInfo(dati);
        if (!dati.visualizzabile) return;
        setPagine(new Array(dati.n_pagine).fill(null));
        for (let n = 1; n <= dati.n_pagine; n += 1) {
          const p = await fetch(`${base}/pagine/${n}`, { headers: intestazioni, signal: controller.signal, cache: 'no-store' });
          if (!p.ok) { if (attivo) setErrore('Una pagina non si e\' potuta caricare. Riprova tra poco.'); return; }
          const blob = await p.blob();
          if (!attivo) return;
          const url = URL.createObjectURL(blob);
          urlCreati.current.push(url);
          setPagine((prec) => { const copia = prec.slice(); copia[n - 1] = url; return copia; });
        }
      } catch (e) {
        if (e.name !== 'AbortError' && attivo) setErrore('Errore di connessione. Riprova tra poco.');
      }
    })();

    return () => { attivo = false; controller.abort(); pulisci(); };
  }, [open, documentId, condominiumId, token, base, pulisci, ricarica]);

  // Alla chiusura non resta nessun blob in memoria.
  useEffect(() => { if (!open) { pulisci(); setPagine([]); setInfo(null); setErroreConferma(null); } }, [open, pulisci]);

  // Numero di pagina in vista, a ogni scorrimento.
  const alloScorrimento = () => {
    const box = contenitore.current;
    if (!box) return;
    const figli = box.querySelectorAll('[data-pagina]');
    const meta = box.getBoundingClientRect().top + box.clientHeight / 2;
    let n = 1;
    figli.forEach((f) => { if (f.getBoundingClientRect().top <= meta) n = Number(f.dataset.pagina); });
    setCorrente(n);
  };

  // Scorciatoie di salvataggio e stampa disattivate finche' il visualizzatore e' aperto.
  useEffect(() => {
    if (!open) return undefined;
    const blocca = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'S', 'P'].includes(e.key)) e.preventDefault();
    };
    window.addEventListener('keydown', blocca, true);
    return () => window.removeEventListener('keydown', blocca, true);
  }, [open]);

  const richiestaPresaVisione = !!info?.presa_visione_richiesta;
  const nonDisponibile = !richiestaPresaVisione && ((info && !info.visualizzabile) || errore);

  // Il condomino attesta di aver preso visione: il server registra chi, quando e il testo; poi si rilegge il documento.
  const confermaPresaVisione = async () => {
    setInConferma(true); setErroreConferma(null);
    try {
      const r = await fetch(`${API_URL}/api/condominiums/${condominiumId}/presa-visione/documenti/${encodeURIComponent(documentId)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setErroreConferma(e.detail || 'Non e\' stato possibile registrare la presa visione. Riprova.');
        return;
      }
      setRicarica((n) => n + 1);
    } catch (e) {
      setErroreConferma('Errore di connessione. Riprova tra poco.');
    } finally {
      setInConferma(false);
    }
  };

  // Download: solo dei verbali, solo dopo la presa visione (lo controlla anche il server).
  const scarica = async () => {
    try {
      const r = await fetch(`${API_URL}/api/condominiums/${condominiumId}/documents/${encodeURIComponent(documentId)}/download`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { setErrore('Il download non e\' disponibile. Riprova tra poco.'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = info?.nome_file || 'verbale.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErrore('Il download non e\' disponibile. Riprova tra poco.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="vd-radice max-w-4xl w-[96vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden">
        {/* La stampa del browser produce una pagina vuota finche' il visualizzatore e' aperto */}
        <style>{'@media print { body { display: none !important; } }'}</style>
        <DialogHeader className="px-5 pt-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 pr-8 text-base">
            <Eye className="h-4 w-4 text-gray-500" />
            {info?.titolo || titoloIniziale || 'Documento'}
            {info?.scaricabile && (
              <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={scarica} data-testid="scarica-verbale">
                <Download className="mr-1 h-4 w-4" /> Scarica
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {richiestaPresaVisione
              ? 'Prima di aprire il verbale serve la tua presa visione.'
              : (info?.scaricabile ? 'Puoi consultare il verbale qui e scaricarne una copia.' : 'Documento in sola consultazione. Non si può scaricare né stampare.')}
            {info?.visualizzabile && info.n_pagine > 0 && ` Pagina ${corrente} di ${info.n_pagine}.`}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={contenitore}
          onScroll={alloScorrimento}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          className="flex-1 overflow-y-auto bg-gray-100 px-3 py-4"
          style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
        >
          {!info && !errore && (
            <div className="h-full flex items-center justify-center text-sm text-gray-500 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Apertura del documento...
            </div>
          )}

          {richiestaPresaVisione && (
            <div className="h-full flex items-center justify-center px-4" data-testid="presa-visione">
              <div className="max-w-lg w-full bg-white rounded-lg shadow p-6 space-y-4 text-center">
                <ClipboardCheck className="h-9 w-9 text-green-700 mx-auto" />
                <h3 className="text-base font-semibold">Presa visione del verbale</h3>
                <p className="text-sm text-gray-700 text-justify">{info.testo_attestazione}</p>
                <p className="text-xs text-gray-500">Il clic viene registrato con il tuo nome, la data e l'ora. Dopo potrai aprire il verbale e scaricarlo.</p>
                {erroreConferma && <p className="text-sm text-red-700">{erroreConferma}</p>}
                <Button type="button" onClick={confermaPresaVisione} disabled={inConferma} className="w-full whitespace-normal h-auto py-2">
                  {inConferma ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Attesto di aver preso visione e ricevuto copia del verbale
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Non ora</Button>
              </div>
            </div>
          )}

          {nonDisponibile && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 px-6">
              <FileX className="h-10 w-10 text-gray-400" />
              <p className="text-sm text-gray-700 max-w-md">
                {errore || info?.messaggio || 'Questo documento non si puo\' visualizzare in app.'}
              </p>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Chiudi</Button>
            </div>
          )}

          {info?.visualizzabile && !errore && (
            <div className="mx-auto max-w-3xl space-y-4">
              {pagine.map((url, i) => (
                <div
                  key={i}
                  data-pagina={i + 1}
                  className="relative bg-white shadow rounded overflow-hidden"
                  style={url ? undefined : { minHeight: 420 }}
                >
                  {url ? (
                    <>
                      <img
                        src={url}
                        alt={`Pagina ${i + 1} di ${info.n_pagine}`}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        onContextMenu={(e) => e.preventDefault()}
                        className="block w-full h-auto"
                        style={{ userSelect: 'none', WebkitUserDrag: 'none', pointerEvents: 'none' }}
                      />
                      {/* Velo trasparente: intercetta tocco lungo e clic destro */}
                      <div className="absolute inset-0" onContextMenu={(e) => e.preventDefault()} />
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Pagina {i + 1}
                    </div>
                  )}
                </div>
              ))}
              {info.messaggio && <p className="text-xs text-center text-gray-500 pb-2">{info.messaggio}</p>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Elenco degli allegati di una comunicazione: ogni voce e' una riga cliccabile
 * «Visualizza documento» (con il titolo, se il server lo da') che apre il
 * visualizzatore. Un allegato che l'utente non puo' vedere resta una riga
 * spenta, senza dire altro.
 */
export function ElencoAllegati({ condominiumId, ids }) {
  const { token } = useAuth();
  const [titoli, setTitoli] = useState({});   // id -> {titolo, ok}
  const [aperto, setAperto] = useState(null);

  useEffect(() => {
    if (!token || !condominiumId || !ids?.length) return undefined;
    const controller = new AbortController();
    (async () => {
      for (const id of ids) {
        try {
          const r = await fetch(
            `${API_URL}/api/condominiums/${condominiumId}/consultazione/documenti/${encodeURIComponent(id)}/info`,
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal },
          );
          const d = r.ok ? await r.json() : null;
          setTitoli((prec) => ({ ...prec, [id]: { ok: !!d, titolo: d?.titolo || null } }));
        } catch (e) {
          if (e.name === 'AbortError') return;
          setTitoli((prec) => ({ ...prec, [id]: { ok: false, titolo: null } }));
        }
      }
    })();
    return () => controller.abort();
  }, [token, condominiumId, ids]);

  if (!ids?.length) return null;
  return (
    <>
      <ul className="mt-3 space-y-1.5">
        {ids.map((id, i) => {
          const t = titoli[id];
          const spento = t && !t.ok;
          return (
            <li key={id}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={spento}
                onClick={() => setAperto(id)}
                className="h-auto min-h-8 w-full justify-start gap-2 whitespace-normal text-left font-normal"
              >
                <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="flex-1">
                  {spento ? 'Documento non disponibile' : (t?.titolo || `Documento ${i + 1}`)}
                </span>
                {!spento && <span className="text-xs text-blue-700 shrink-0">Visualizza documento</span>}
              </Button>
            </li>
          );
        })}
      </ul>
      <VisualizzatoreDocumento
        open={!!aperto}
        onOpenChange={(v) => { if (!v) setAperto(null); }}
        condominiumId={condominiumId}
        documentId={aperto}
        titoloIniziale={aperto ? titoli[aperto]?.titolo : undefined}
      />
    </>
  );
}
